import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getJournalEntryById } from '../../services/bennyJournalService';
import { getAllWikiEntries } from '../../services/wikiService';
import { linkifyWikiEntries } from '../../utils/wikiLinkUtils';

const BennyJournalEntryView = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wikiEntries, setWikiEntries] = useState([]);

  // Get referrer information from location state
  const referrer = location.state?.from;

  useEffect(() => {
    const fetchEntry = async () => {
      if (!entryId) return;
      
      setLoading(true);
      setError('');
      try {
        const [entryData, wikiData] = await Promise.all([
          getJournalEntryById(entryId),
          getAllWikiEntries()
        ]);
        
        if (entryData) {
          setEntry(entryData);
        } else {
          setError('Journal entry not found.');
        }
        
        if (wikiData) {
          setWikiEntries(wikiData);
        }
      } catch (err) {
        console.error("Error fetching journal entry:", err);
        setError('Failed to load journal entry. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEntry();
  }, [entryId]);

  const handleBackNavigation = () => {
    if (referrer) {
      // Navigate back to the specific referrer
      navigate(referrer);
    } else {
      // Default fallback to journal list
      navigate('/benny-journal');
    }
  };
  const getBackButtonText = () => {
    if (referrer?.includes('/campaign-episodes/')) {
      return 'Back to Episode Details';
    } else if (referrer === '/campaign-episodes') {
      return 'Back to Campaign Episodes';
    } else if (referrer === '/dashboard') {
      return 'Back to Dashboard';
    } else {
      return 'Back to Journal List';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date unknown';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    return isNaN(date.getTime()) ? 'Date unknown' : date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#8b4513' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ '.MuiAlert-message': { color: 'white' }, backgroundColor: '#5c3c10' }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackNavigation}
          sx={{ mt: 2, color: '#8b4513', borderColor: '#8b4513' }}
          variant="outlined"
        >
          {getBackButtonText()}
        </Button>
      </Container>
    );
  }

  if (!entry) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ '.MuiAlert-message': { color: 'white' }, backgroundColor: '#8b4513' }}>
          Journal entry not found.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackNavigation}
          sx={{ mt: 2, color: '#8b4513', borderColor: '#8b4513' }}
          variant="outlined"
        >
          {getBackButtonText()}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, color: 'white' }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBackNavigation}
        sx={{ 
          mb: 3, 
          color: '#8b4513', 
          borderColor: '#8b4513',
          '&:hover': {
            backgroundColor: 'rgba(139, 69, 19, 0.1)',
            borderColor: '#a0522d'
          }
        }}
        variant="outlined"
      >
        {getBackButtonText()}
      </Button>

      {/* Journal Entry Content */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: '#fffacd', 
          border: '2px solid #d2b48c',
          borderRadius: '8px',
          color: '#5c3c10'
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontFamily: '"Georgia", serif', 
            color: '#8b4513',
            mb: 2,
            textAlign: 'center',
            borderBottom: '2px solid #d2b48c',
            pb: 2
          }}
        >
          {entry.title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, fontSize: '0.9rem', fontStyle: 'italic' }}>
          <Typography variant="body2" sx={{ color: '#7a5c3f', fontFamily: '"Georgia", serif' }}>
            Authored by: {entry.userName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#7a5c3f', fontFamily: '"Georgia", serif' }}>
            Created: {formatDate(entry.createdAt)}
          </Typography>
        </Box>

        {entry.description && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#8b4513', 
                fontFamily: '"Georgia", serif',
                mb: 2
              }}
            >
              Description
            </Typography>
            <Typography 
              component="div"
              variant="body1" 
              sx={{ 
                fontFamily: '"Georgia", serif', 
                whiteSpace: 'pre-wrap', 
                color: '#5c3c10',
                lineHeight: 1.6,
                backgroundColor: '#f5f5dc',
                p: 2,
                borderRadius: 1,
                border: '1px solid #d2b48c'
              }}
            >
              {linkifyWikiEntries(entry.description, wikiEntries)}
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mt: 4 }}>
          {entry.downloadURL && (
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => navigate(`/view-pdf/${entryId}`, { 
                state: { 
                  pdfUrl: entry.downloadURL,
                  journalTitle: entry.title,
                  from: location.pathname // Pass current location for PDF viewer back navigation
                } 
              })}
              sx={{
                fontFamily: '"Georgia", serif',
                backgroundColor: '#8b4513',
                color: '#F5F5F5',
                '&:hover': { 
                  backgroundColor: '#a0522d'
                }
              }}
            >
              View PDF Document
            </Button>
          )}

          {entry.microsoftLink && (
            <Button
              variant="contained"
              href={entry.microsoftLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: '"Georgia", serif',
                backgroundColor: '#556B2F',
                color: '#F5F5F5',
                '&:hover': { backgroundColor: '#6B8E23' }
              }}
            >
              Open in Microsoft Docs
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default BennyJournalEntryView;
